import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Router, RouterOutlet } from '@angular/router';
import { UserRepository } from '@data/user/user.store';
import { UserEventId } from '@data/user/user.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventTrackingService } from '@core/services/event-tracking/event-tracking.service';
import { AppRouteNames } from './app.routing.names';

import { MascotDialogComponent } from '@shared/components/mascot-dialog/mascot-dialog.component';
import { MASCOT_DIALOG_PRIORITY } from '@shared/components/mascot-dialog/mascot-dialog-orchestrator.service';
import { FlexModule } from '@ngbracket/ngx-layout';
import { MatButton } from '@angular/material/button';
import { APP_CONFIG } from '@core/config/app-config.token';
import { brandOf } from '@core/config/brand';
import { isKiosk, logsOutWhenIdle, resumeLink } from '@core/services/kiosk/kiosk';
import { QrCodeComponent } from '@shared/components/qr-code/qr-code.component';
import { FairTabsService } from '@core/services/fair-tabs/fair-tabs.service';
import { ThemeService } from '@core/services/theme/theme.service';
import { MatIcon } from '@angular/material/icon';
import { SafePipe } from '@shared/pipes/safe.pipe';
import { MascotService } from '@core/services/mascot/mascot.service';

@UntilDestroy()
@Component({
  selector: 'recrewt-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    MascotDialogComponent,
    FlexModule,
    MatButton,
    TranslateModule,
    QrCodeComponent,
    MatIcon,
    SafePipe,
  ],
  standalone: true,
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  private router = inject(Router);
  private userRepo = inject(UserRepository);
  private appConfig = inject(APP_CONFIG);
  private eventTracking = inject(EventTrackingService);

  protected readonly fairTabs = inject(FairTabsService);

  protected readonly mascot = inject(MascotService);

  // created with the app: an embedded job app asks its fair's app for light or dark right away
  protected readonly theme = inject(ThemeService);

  protected readonly mascotPriority = MASCOT_DIALOG_PRIORITY;

  title = this.appConfig.app.name;

  isIdle = false;

  isReloadWithId = false;

  loggedIn = false;

  lastPing: Date | null = null;

  /** Kiosk: the idle visitor can still take their list along. */
  handOffLink: string | null = null;

  constructor() {
    const translate = this.translate;

    window.document.title = this.appConfig.app.title;
    translate.addLangs(['de', 'en']);
    translate.setDefaultLang('de');

    const lang = window.localStorage.getItem('lang');
    if (lang && lang !== 'undefined') {
      translate.use(lang);
    }
    this.loadAnalytics();
  }

  ngOnInit() {
    this.eventTracking.init();

    // a phone keeps its visitor: idle logout and the restart question are for shared devices
    if (!logsOutWhenIdle(this.appConfig)) {
      return;
    }

    if (window.localStorage.getItem('loggedInBeforeUnload')) {
      this.isReloadWithId = !new URLSearchParams(window.location.search).has('restart');
      window.localStorage.removeItem('loggedInBeforeUnload');
    }

    this.idle.setIdle(5 * 60);
    this.idle.setTimeout(25 * 60);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      this.resetOnMove();
    });

    this.idle.onTimeout.subscribe(() => {
      this.logout();
    });

    this.idle.onIdleStart.subscribe(() => {
      if (!this.loggedIn) {
        this.idle.stop();
        return;
      }
      this.handOffLink = isKiosk() ? resumeLink() : null;
      this.isIdle = true;
    });

    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    this.userRepo.hasUser$.pipe(untilDestroyed(this)).subscribe((hasUser) => {
      this.loggedIn = hasUser;
      if (hasUser) {
        this.invalidateCacheOnTimeout();
      }
      if (this.loggedIn) {
        window.localStorage.setItem('loggedInBeforeUnload', 'true');
      } else {
        window.localStorage.removeItem('loggedInBeforeUnload');
      }
    });

    this.reset();
  }

  public loadAnalytics() {
    if (!this.appConfig.app.trackingId) {
      return;
    }
    let script = document.createElement('script');
    script.src = `${brandOf(this.appConfig).analyticsHost}/script.js`;
    script.dataset['websiteId'] = this.appConfig.app.trackingId;
    script.dataset['do-not-track'] = 'true';
    script.dataset['tag'] = this.appConfig.app.name;
    script.defer = true;
    script.innerHTML = '';
    document.body.appendChild(script);
  }

  logout() {
    this.eventTracking.track(UserEventId.APP_LOGGED_OUT);
    this.eventTracking.flush();
    this.userRepo.logoutUser();
    this.router.navigate([AppRouteNames.HOME]).then(() => (this.isReloadWithId = false));
    this.isIdle = false;
  }

  reset() {
    this.idle.watch();
    this.isIdle = false;
  }

  private invalidateCacheOnTimeout() {
    const lastSign = window.localStorage.getItem('ng2Idle.main.expiry');
    if (!lastSign) {
      return;
    }

    const diff = Date.now() - parseInt(lastSign);
    if (diff <= 1000 * 60 * 30 /* 30 minutes */) {
      return;
    }
    this.logout();
  }

  private resetOnMove() {
    if (this.isIdle) {
      return;
    }

    if (this.loggedIn) {
      this.reset();
    } else {
      this.idle.stop();
    }
  }
}
